import { TEXT_EDITOR_LIMIT } from '../files/fileTypes';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { createHelpTip } from '../help/helpTips';
import type { SharedFolderFileEntry } from '../crypto/sharedFolderManifest';
import { bindModalDismiss, createModalCloseButton } from './modalDialogShell';

export const TEXT_EDITOR_AUTOSAVE_MS = 2000;

export interface FileTextEditorHandle {
  fileId: string;
  isOpen(): boolean;
  needsRemote(entry: SharedFolderFileEntry): boolean;
  applyRemote(text: string, entry: SharedFolderFileEntry): void;
  markDeleted(): void;
  close(): void;
}

export interface ShowFileTextEditorOptions {
  fileName: string;
  fileId: string;
  initialText: string;
  entry: SharedFolderFileEntry;
  save(text: string): Promise<SharedFolderFileEntry>;
  onClose?: () => void;
}

export function showFileTextEditor(options: ShowFileTextEditorOptions): FileTextEditorHandle {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  backdrop.setAttribute('role', 'presentation');
  const dialog = document.createElement('div');
  dialog.className = 'modal-dialog file-preview-dialog file-text-editor-dialog';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.innerHTML = `
    <div class="file-preview-header">
      <h2 id="file-text-editor-title"></h2>
      <span class="muted file-text-editor-status" id="file-text-editor-status"></span>
      <div class="file-preview-header-actions">
        <button type="button" class="button primary" id="file-text-editor-save" data-i18n="files.owner.editorSave"></button>
        <label class="file-text-editor-autosave-label">
          <input type="checkbox" id="file-text-editor-autosave" />
          <span data-i18n="files.owner.editorAutosave"></span>
        </label>
        <button type="button" class="button subtle" id="file-text-editor-download" data-i18n="files.owner.download"></button>
      </div>
    </div>
    <div class="file-text-editor-banner warning-panel" id="file-text-editor-banner" hidden>
      <span id="file-text-editor-banner-text"></span>
      <div class="file-text-editor-banner__actions">
        <button type="button" class="button ghost" id="file-text-editor-load-remote"></button>
        <button type="button" class="button ghost" id="file-text-editor-keep-mine"></button>
      </div>
    </div>
    <textarea class="file-text-editor-textarea" id="file-text-editor-textarea" spellcheck="false"></textarea>
  `;
  const headerActions = dialog.querySelector('.file-preview-header-actions') as HTMLDivElement;
  headerActions.prepend(createHelpTip(dialogHelpTopic('fileTextEditor')));
  headerActions.append(createModalCloseButton({ id: 'file-text-editor-close', className: 'file-preview-close' }));
  backdrop.append(dialog);
  document.body.append(backdrop);

  const titleEl = dialog.querySelector('#file-text-editor-title') as HTMLElement;
  const statusEl = dialog.querySelector('#file-text-editor-status') as HTMLElement;
  const bannerEl = dialog.querySelector('#file-text-editor-banner') as HTMLElement;
  const bannerTextEl = dialog.querySelector('#file-text-editor-banner-text') as HTMLElement;
  const loadRemoteButton = dialog.querySelector('#file-text-editor-load-remote') as HTMLButtonElement;
  const keepMineButton = dialog.querySelector('#file-text-editor-keep-mine') as HTMLButtonElement;
  const textarea = dialog.querySelector('#file-text-editor-textarea') as HTMLTextAreaElement;
  textarea.value = options.initialText;

  const saveButton = dialog.querySelector('#file-text-editor-save') as HTMLButtonElement;
  const autosaveCheckbox = dialog.querySelector('#file-text-editor-autosave') as HTMLInputElement;
  let autosaveEnabled = false;

  let open = true;
  let deleted = false;
  let saving = false;
  let queued = false;
  let saveTimer: number | null = null;
  let lastSavedText = options.initialText;
  let lastSavedBlobId = options.entry.blobId;
  let lastSavedHash = options.entry.contentHash;
  let pendingRemote: { text: string; entry: SharedFolderFileEntry } | null = null;
  let statusKind: 'saved' | 'saving' | 'unsaved' | 'remote' | 'deleted' | 'tooLarge' | 'saveFailed' = 'saved';

  const refreshLocale = () => {
    titleEl.textContent = options.fileName;
    applyI18n(dialog);
    loadRemoteButton.textContent = t('files.owner.editorLoadRemote');
    keepMineButton.textContent = t('files.owner.editorKeepMine');
    bannerTextEl.textContent = t('files.owner.editorRemoteUpdate');
    statusEl.textContent = statusLabel();
  };

  const statusLabel = (): string => {
    switch (statusKind) {
      case 'saving':
        return t('files.owner.editorSaving');
      case 'unsaved':
        return t('files.owner.editorUnsaved');
      case 'remote':
        return t('files.owner.editorRemoteUpdate');
      case 'deleted':
        return t('files.owner.editorDeleted');
      case 'tooLarge':
        return t('files.owner.editorTooLarge');
      case 'saveFailed':
        return t('files.owner.editorSaveFailed');
      default:
        return t('files.owner.editorSaved');
    }
  };

  const setStatus = (kind: typeof statusKind) => {
    statusKind = kind;
    statusEl.textContent = statusLabel();
  };

  const hideBanner = () => {
    bannerEl.hidden = true;
    pendingRemote = null;
  };

  const showBanner = () => {
    bannerEl.hidden = false;
    setStatus('remote');
  };

  const encodedSize = (text: string): number => new TextEncoder().encode(text).length;

  const runSave = async (): Promise<boolean> => {
    if (!open || deleted) {
      return true;
    }

    if (saveTimer !== null) {
      window.clearTimeout(saveTimer);
      saveTimer = null;
    }

    const text = textarea.value;
    if (text === lastSavedText) {
      if (!pendingRemote) {
        setStatus('saved');
      }
      return true;
    }

    if (encodedSize(text) > TEXT_EDITOR_LIMIT) {
      setStatus('tooLarge');
      return false;
    }

    if (saving) {
      queued = true;
      return false;
    }

    saving = true;
    setStatus('saving');
    try {
      const entry = await options.save(text);
      lastSavedText = text;
      lastSavedBlobId = entry.blobId;
      lastSavedHash = entry.contentHash;
      if (textarea.value === text) {
        setStatus(pendingRemote ? 'remote' : 'saved');
        saveButton.disabled = true;
      } else {
        setStatus('unsaved');
        saveButton.disabled = false;
      }
      return true;
    } catch (cause) {
      if (cause instanceof Error && cause.message === 'file_deleted') {
        markDeleted();
        return false;
      }
      setStatus('saveFailed');
      return false;
    } finally {
      saving = false;
      if (queued && open && !deleted) {
        queued = false;
        void runSave();
      }
    }
  };

  const scheduleSave = () => {
    if (!open || deleted) {
      return;
    }

    if (textarea.value === lastSavedText) {
      if (!pendingRemote) {
        setStatus('saved');
      }
      if (saveTimer !== null) {
        window.clearTimeout(saveTimer);
        saveTimer = null;
      }
      saveButton.disabled = true;
      return;
    }

    setStatus('unsaved');
    saveButton.disabled = false;
    if (!autosaveEnabled) {
      return;
    }
    if (saveTimer !== null) {
      window.clearTimeout(saveTimer);
    }
    saveTimer = window.setTimeout(() => {
      saveTimer = null;
      void runSave();
    }, TEXT_EDITOR_AUTOSAVE_MS);
  };

  const loadRemote = () => {
    if (!pendingRemote) {
      return;
    }

    const { text, entry } = pendingRemote;
    textarea.value = text;
    lastSavedText = text;
    lastSavedBlobId = entry.blobId;
    lastSavedHash = entry.contentHash;
    hideBanner();
    setStatus('saved');
  };

  const markDeleted = () => {
    deleted = true;
    textarea.readOnly = true;
    hideBanner();
    setStatus('deleted');
  };

  const close = async (force = false) => {
    if (!open) {
      return;
    }

    if (!force && !deleted && textarea.value !== lastSavedText) {
      if (!confirm(t('files.owner.editorUnsavedClose'))) {
        return;
      }
    }

    open = false;
    if (saveTimer !== null) {
      window.clearTimeout(saveTimer);
      saveTimer = null;
    }
    window.removeEventListener('keydown', onKeyDown);
    unbindDismiss();
    unbindLocale();
    backdrop.remove();
    options.onClose?.();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 's' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void runSave();
    }
  };

  const unbindDismiss = bindModalDismiss({
    backdrop,
    closeButton: dialog.querySelector('#file-text-editor-close') as HTMLButtonElement,
    onClose: () => {
      void close();
    },
  });

  const unbindLocale = bindLocaleRefresh(refreshLocale);
  refreshLocale();
  saveButton.disabled = true;
  window.addEventListener('keydown', onKeyDown);
  textarea.addEventListener('input', scheduleSave);
  textarea.addEventListener('blur', () => {
    if (autosaveEnabled) {
      void runSave();
    }
  });
  saveButton.addEventListener('click', () => {
    void runSave();
  });
  autosaveCheckbox.addEventListener('change', () => {
    autosaveEnabled = autosaveCheckbox.checked;
    saveButton.hidden = autosaveEnabled;
    if (autosaveEnabled && textarea.value !== lastSavedText) {
      scheduleSave();
    }
  });
  dialog.querySelector('#file-text-editor-download')?.addEventListener('click', () => {
    const bytes = new TextEncoder().encode(textarea.value);
    const blob = new Blob([bytes], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = options.fileName;
    link.click();
    URL.revokeObjectURL(url);
  });
  loadRemoteButton.addEventListener('click', loadRemote);
  keepMineButton.addEventListener('click', () => {
    hideBanner();
    setStatus(textarea.value === lastSavedText ? 'saved' : 'unsaved');
  });
  textarea.focus();

  return {
    fileId: options.fileId,
    isOpen: () => open,
    needsRemote: (entry) => entry.blobId !== lastSavedBlobId && entry.contentHash !== lastSavedHash,
    applyRemote: (text, entry) => {
      if (!open || deleted || (entry.blobId === lastSavedBlobId || entry.contentHash === lastSavedHash)) {
        return;
      }

      pendingRemote = { text, entry };
      if (textarea.value === lastSavedText) {
        loadRemote();
        return;
      }

      showBanner();
    },
    markDeleted,
    close: () => {
      void close(true);
    },
  };
}
